import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';

import { API } from '@/config/api';

export const useTasks = (assignedTo = null) => {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pendingNegotiations, setPendingNegotiations] = useState([]);

  const fetchTasks = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (assignedTo) params.append('assigned_to', assignedTo);
      const response = await axios.get(`${API}/tasks?${params.toString()}`);
      setTasks(Array.isArray(response.data) ? response.data : []);
      setError(null);
    } catch (err) {
      console.error('Error fetching tasks:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [assignedTo]);

  const fetchPendingNegotiations = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/negotiations/pending/all`);
      setPendingNegotiations(Array.isArray(response.data) ? response.data : []);
    } catch (err) {
      console.error('Error fetching pending negotiations:', err);
    }
  }, []);

  const approveNegotiation = async (negotiationId, managerId, managerResponse = '') => {
    try {
      await axios.post(`${API}/negotiations/${negotiationId}/approve?manager_id=${managerId}&manager_response=${managerResponse}`);
      await fetchPendingNegotiations();
      await fetchTasks();
      return { success: true };
    } catch (err) {
      return { success: false, error: err.message };
    }
  };

  const rejectNegotiation = async (negotiationId, managerId, managerResponse = '') => {
    try {
      await axios.post(`${API}/negotiations/${negotiationId}/reject?manager_id=${managerId}&manager_response=${managerResponse}`);
      await fetchPendingNegotiations();
      return { success: true };
    } catch (err) {
      return { success: false, error: err.message };
    }
  };

  useEffect(() => {
    fetchTasks();
    fetchPendingNegotiations();
    const interval = setInterval(() => {
      fetchTasks();
      fetchPendingNegotiations();
    }, 30000);
    return () => clearInterval(interval);
  }, [fetchTasks, fetchPendingNegotiations]);

  const createTask = async (taskData) => {
    try {
      const response = await axios.post(`${API}/tasks`, taskData);
      await fetchTasks();
      // Handle both single task and multi-task response
      if (response.data.tasks) {
        return { success: true, tasks: response.data.tasks, message: response.data.message };
      }
      return { success: true, task: response.data };
    } catch (err) {
      return { success: false, error: err.message };
    }
  };

  const updateTask = async (taskId, taskData) => {
    try {
      const response = await axios.put(`${API}/tasks/${taskId}`, taskData);
      await fetchTasks();
      return { success: true, task: response.data };
    } catch (err) {
      return { success: false, error: err.message };
    }
  };

  const updateTaskStatus = async (taskId, status, pendingReason = null) => {
    try {
      let url = `${API}/tasks/${taskId}/status?status=${status}`;
      if (status === 'pending' && pendingReason) {
        url += `&pending_reason=${encodeURIComponent(pendingReason)}`;
      }
      await axios.patch(url);
      await fetchTasks();
      return { success: true };
    } catch (err) {
      return { success: false, error: err.message };
    }
  };

  const deleteTask = async (taskId) => {
    try {
      await axios.delete(`${API}/tasks/${taskId}`);
      await fetchTasks();
      return { success: true };
    } catch (err) {
      return { success: false, error: err.message };
    }
  };

  return {
    tasks,
    loading,
    error,
    pendingNegotiations,
    refetch: fetchTasks,
    refetchNegotiations: fetchPendingNegotiations,
    createTask,
    updateTask,
    updateTaskStatus,
    deleteTask,
    approveNegotiation,
    rejectNegotiation
  };
};
